import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TodayIcon from "@mui/icons-material/Today";
import DownloadIcon from "@mui/icons-material/Download";

import dayjs from "dayjs";
import "dayjs/locale/es";
import isoWeek from "dayjs/plugin/isoWeek";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { db } from "../../../firebase";
import {
  collection,
  getDocs,
  query,
  where,
  documentId,
} from "firebase/firestore";

import * as XLSX from "xlsx";
import moment from "moment";
import { useNavigate } from "react-router-dom";

dayjs.locale("es");
dayjs.extend(isoWeek);

/**
 * 🔧 Ajusta esto al nombre real de tu colección de asistencias
 * (por ejemplo: "attendance", "xpress_ebiexAttendanceSessions", etc.)
 */
const ATTENDANCE_COL = "xpress_ebiexAttendance";

// -------------------------------
// Helpers
// -------------------------------
const chunk = (arr, size = 10) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

const minutesFromHHmm = (hhmm) => {
  if (!hhmm) return null;
  const m = String(hhmm).match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
  return hh * 60 + mm;
};

const diffMinutes = (aMin, bMin) => {
  if (aMin == null || bMin == null) return null;
  return aMin - bMin;
};

const toHHmm = (d) => {
  try {
    // Firestore Timestamp -> toDate
    if (d?.toDate) return dayjs(d.toDate()).format("HH:mm");
    // Date
    if (d instanceof Date) return dayjs(d).format("HH:mm");
    // ISO string
    const x = dayjs(d);
    return x.isValid() ? x.format("HH:mm") : "";
  } catch {
    return "";
  }
};

const toYYYYMMDD = (d) => {
  try {
    if (d?.toDate) return dayjs(d.toDate()).format("YYYY-MM-DD");
    if (d instanceof Date) return dayjs(d).format("YYYY-MM-DD");
    const x = dayjs(d);
    return x.isValid() ? x.format("YYYY-MM-DD") : "";
  } catch {
    return "";
  }
};

/**
 * Normaliza un doc de asistencia a un formato usable en reportes.
 * Ajusta aquí si tu schema es distinto.
 */
const normalizeAttendanceDoc = (raw) => {
  const driverId = String(raw.driverId || raw.uid || raw.riderId || "").trim();
  const storeId = String(
    raw.storeId || raw.workStoreId || raw.store || ""
  ).trim();

  // date puede venir como string YYYY-MM-DD o timestamp
  const date =
    String(raw.date || "").match(/^\d{4}-\d{2}-\d{2}$/)?.[0] ||
    toYYYYMMDD(raw.dateTs || raw.day || raw.startedAt || raw.startAt) ||
    "";

  // entrada/salida:
  const startTime =
    String(raw.startTime || "") ||
    toHHmm(raw.startedAt || raw.startAt || raw.checkInAt) ||
    "";

  const endTime =
    String(raw.endTime || "") ||
    toHHmm(raw.endedAt || raw.endAt || raw.checkOutAt) ||
    "";

  // flags geo (si existen)
  const startInside =
    raw.startInsideStore ??
    raw.checkInInside ??
    raw.computedInsideWorkStore ??
    null;

  const endInside = raw.endInsideStore ?? raw.checkOutInside ?? null;

  // movilizaciones/visitas (si existen)
  const mobilizations = Array.isArray(raw.mobilizations)
    ? raw.mobilizations
    : Array.isArray(raw.events)
    ? raw.events
    : [];

  return {
    id: raw.id || raw.attendanceId || raw.sessionId || "",
    driverId,
    storeId,
    date,
    startTime,
    endTime,
    startInside,
    endInside,
    mobilizations,
    raw,
  };
};

const SHIFT_STATUSES = new Set([
  "SCHEDULED",
  "STARTED",
  "FINISHED",
  "CANCELLED",
  "NO_SHOW",
]);

const normalizeShiftStatus = (v) => {
  const s = String(v || "SCHEDULED")
    .trim()
    .toUpperCase();
  return SHIFT_STATUSES.has(s) ? s : "SCHEDULED";
};

const statusMeta = (status) => {
  const s = normalizeShiftStatus(status);

  if (s === "SCHEDULED" || s === "STARTED") {
    return {
      label: s,
      bg: "rgba(25,118,210,0.15)",
      fg: "#1565C0",
      bd: "rgba(21,101,192,0.35)",
    };
  }

  if (s === "FINISHED") {
    return {
      label: s,
      bg: "rgba(46,125,50,0.15)",
      fg: "#2E7D32",
      bd: "rgba(46,125,50,0.35)",
    };
  }

  if (s === "CANCELLED" || s === "NO_SHOW") {
    return {
      label: s,
      bg: "rgba(198,40,40,0.15)",
      fg: "#C62828",
      bd: "rgba(198,40,40,0.35)",
    };
  }

  return {
    label: s,
    bg: "rgba(0,0,0,0.06)",
    fg: "#111",
    bd: "rgba(0,0,0,0.15)",
  };
};

const metaChip = (kind) => {
  if (kind === "ok")
    return {
      bg: "rgba(46,125,50,0.15)",
      fg: "#2E7D32",
      bd: "rgba(46,125,50,0.35)",
    };
  if (kind === "warn")
    return {
      bg: "rgba(245,124,0,0.15)",
      fg: "#E65100",
      bd: "rgba(230,81,0,0.35)",
    };
  if (kind === "bad")
    return {
      bg: "rgba(198,40,40,0.15)",
      fg: "#C62828",
      bd: "rgba(198,40,40,0.35)",
    };
  return { bg: "rgba(0,0,0,0.06)", fg: "#111", bd: "rgba(0,0,0,0.15)" };
};

// -------------------------------
// Component
// -------------------------------
const Asistencia = () => {
  const [month, setMonth] = useState(dayjs());
  const [loading, setLoading] = useState(false);

  // data base
  const [shifts, setShifts] = useState([]);
  const [attendances, setAttendances] = useState([]);
  const [driverMap, setDriverMap] = useState(new Map());
  const [storeMap, setStoreMap] = useState(new Map());

  // filtros
  const [filterStoreId, setFilterStoreId] = useState("");
  const [filterDriverId, setFilterDriverId] = useState("");
  const [filterCompliance, setFilterCompliance] = useState(""); // "", OK, WARN, BAD, NO_ATT

  // UI
  const [toast, setToast] = useState({
    open: false,
    msg: "",
    severity: "success",
  });

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRow, setDetailRow] = useState(null);

  const monthLabel = useMemo(() => month.format("MMMM YYYY"), [month]);

  const navigate = useNavigate();

  const loadMonth = useCallback(async () => {
    setLoading(true);
    try {
      const start = month.startOf("month").format("YYYY-MM-DD");
      const end = month.endOf("month").format("YYYY-MM-DD");

      // 1) turnos
      const qShifts = query(
        collection(db, "xpress_ebiexShifts"),
        where("date", ">=", start),
        where("date", "<=", end)
      );
      const snap = await getDocs(qShifts);
      const monthShifts = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setShifts(monthShifts);

      // 2) asistencias (por date string igual que shifts)
      const qAtt = query(
        collection(db, ATTENDANCE_COL),
        where("date", ">=", start),
        where("date", "<=", end)
      );
      const attSnap = await getDocs(qAtt);
      const monthAtt = attSnap.docs.map((d) =>
        normalizeAttendanceDoc({ id: d.id, ...d.data() })
      );
      setAttendances(monthAtt);

      // 3) ids usados para nombres
      const driverIds = Array.from(
        new Set([
          ...monthShifts.map((s) => s.driverId).filter(Boolean),
          ...monthAtt.map((a) => a.driverId).filter(Boolean),
        ])
      );

      const storeIds = Array.from(
        new Set([
          ...monthShifts.map((s) => s.storeId).filter(Boolean),
          ...monthAtt.map((a) => a.storeId).filter(Boolean),
        ])
      );

      // drivers
      const dMap = new Map();
      for (const part of chunk(driverIds, 10)) {
        const qDrivers = query(
          collection(db, "drivers"),
          where(documentId(), "in", part)
        );
        const dSnap = await getDocs(qDrivers);
        dSnap.docs.forEach((x) => {
          const data = x.data() || {};
          const name =
            data.name ||
            data.fullName ||
            data.displayName ||
            data.email ||
            x.id;
          dMap.set(x.id, { id: x.id, name, ...data });
        });
      }

      // stores
      const sMap = new Map();
      for (const part of chunk(storeIds, 10)) {
        const qStores = query(
          collection(db, "xpress_ebiexStores"),
          where(documentId(), "in", part)
        );
        const sSnap = await getDocs(qStores);
        sSnap.docs.forEach((x) => {
          const data = x.data() || {};
          const name = data.name || x.id;
          sMap.set(x.id, { id: x.id, name, ...data });
        });
      }

      setDriverMap(dMap);
      setStoreMap(sMap);
    } catch (e) {
      console.error("loadMonth asistencia error:", e);
      setShifts([]);
      setAttendances([]);
      setDriverMap(new Map());
      setStoreMap(new Map());
      setToast({
        open: true,
        severity: "error",
        msg: `Error cargando data: ${e?.code || "error"}`,
      });
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    loadMonth();
  }, [loadMonth]);

  const storeOptions = useMemo(() => {
    return Array.from(storeMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [storeMap]);

  const driverOptions = useMemo(() => {
    return Array.from(driverMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [driverMap]);

  /**
   * Merge base:
   * - Por cada turno programado buscamos asistencia del mismo (driverId + storeId + date)
   * - Si no hay asistencia -> NO_ATT
   */
  const reportRows = useMemo(() => {
    const attIndex = new Map();
    for (const a of attendances) {
      const key = `${a.date}__${a.storeId}__${a.driverId}`;
      if (!attIndex.has(key)) attIndex.set(key, []);
      attIndex.get(key).push(a);
    }

    // tolerancias (primera versión)
    const TOL_MIN = 10; // horario entrada/salida +/- 10 minutos
    const TOL_HOURS_MIN = 10; // horas cumplidas margen 10 minutos

    const rows = (shifts || []).map((s) => {
      const key = `${s.date}__${s.storeId}__${s.driverId}`;
      const matches = attIndex.get(key) || [];
      const a = matches[0] || null; // si hay varias, primera (luego refinamos)

      // base info
      const schedStartMin = minutesFromHHmm(s.startTime);
      const schedEndMin = minutesFromHHmm(s.endTime);
      const schedDurMin =
        schedStartMin != null && schedEndMin != null
          ? Math.max(0, schedEndMin - schedStartMin)
          : null;

      const attStartMin = minutesFromHHmm(a?.startTime);
      const attEndMin = minutesFromHHmm(a?.endTime);
      const attDurMin =
        attStartMin != null && attEndMin != null
          ? Math.max(0, attEndMin - attStartMin)
          : null;

      // checks
      const hasAttendance = !!a;

      const shiftStatus = normalizeShiftStatus(s.status);

      const inHorario =
        hasAttendance &&
        schedStartMin != null &&
        schedEndMin != null &&
        attStartMin != null &&
        attEndMin != null
          ? Math.abs(diffMinutes(attStartMin, schedStartMin) ?? 99999) <=
              TOL_MIN &&
            Math.abs(diffMinutes(attEndMin, schedEndMin) ?? 99999) <= TOL_MIN
          : null;

      const horasCumplidas =
        hasAttendance && schedDurMin != null && attDurMin != null
          ? attDurMin + TOL_HOURS_MIN >= schedDurMin
          : null;

      // geo: si existen flags startInside/endInside, si no -> null
      const geoOk =
        hasAttendance && (a?.startInside !== null || a?.endInside !== null)
          ? a?.startInside !== false && a?.endInside !== false
          : null;

      const cierreFuera =
        hasAttendance && a?.endInside === false ? true : false;

      const cerroDentro = geoOk === null ? "" : cierreFuera ? "NO" : "SI";

      const tiempoExtra =
        hasAttendance && schedDurMin != null && attDurMin != null
          ? attDurMin > schedDurMin + TOL_HOURS_MIN
          : false;

      // compliance bucket
      let compliance = "NO_ATT";

      if (shiftStatus === "CANCELLED") {
        // Si está cancelado, no exigimos asistencia
        compliance = "CANCELLED";
      } else if (shiftStatus === "NO_SHOW") {
        // Si era NO_SHOW, lo marcamos como NO_SHOW (si hay asistencia, es raro)
        compliance = hasAttendance ? "WARN" : "NO_SHOW";
      } else {
        // SCHEDULED / STARTED / FINISHED
        if (!hasAttendance) {
          compliance = "NO_ATT";
        } else {
          const bad =
            inHorario === false || horasCumplidas === false || geoOk === false;
          const warn = tiempoExtra || cierreFuera;
          compliance = bad ? "BAD" : warn ? "WARN" : "OK";
        }
      }
      if (hasAttendance) {
        // BAD si algo crítico falla
        const bad =
          inHorario === false || horasCumplidas === false || geoOk === false;
        const warn = tiempoExtra || cierreFuera;
        compliance = bad ? "BAD" : warn ? "WARN" : "OK";
      }

      return {
        // ids
        shiftId: s.id,
        attendanceId: a?.id || "",
        date: s.date,
        storeId: s.storeId,
        driverId: s.driverId,

        // programado
        scheduledStart: s.startTime || "",
        scheduledEnd: s.endTime || "",
        scheduledMinutes: schedDurMin,

        // real
        realStart: a?.startTime || "",
        realEnd: a?.endTime || "",
        realMinutes: attDurMin,

        // checks
        inHorario,
        horasCumplidas,
        geoOk,
        cerroDentro,
        tiempoExtra,
        compliance,
        shiftStatus,
        // raw refs
        shift: s,
        attendance: a,
      };
    });

    return rows;
  }, [shifts, attendances]);

  const filteredRows = useMemo(() => {
    return reportRows.filter((r) => {
      if (filterStoreId && r.storeId !== filterStoreId) return false;
      if (filterDriverId && r.driverId !== filterDriverId) return false;
      if (filterCompliance && r.compliance !== filterCompliance) return false;
      return true;
    });
  }, [reportRows, filterStoreId, filterDriverId, filterCompliance]);

  const rowsByDate = useMemo(() => {
    const map = new Map();
    for (const r of filteredRows) {
      const key = String(r.date || "");
      if (!key) continue;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    }
    // orden por hora programada
    for (const [k, arr] of map.entries()) {
      arr.sort((a, b) =>
        String(a.scheduledStart || "").localeCompare(
          String(b.scheduledStart || "")
        )
      );
      map.set(k, arr);
    }
    return map;
  }, [filteredRows]);

  const gridDays = useMemo(() => {
    const start = month.startOf("month").startOf("isoWeek");
    const days = [];
    for (let i = 0; i < 42; i++) days.push(start.add(i, "day"));
    return days;
  }, [month]);

  const weekDays = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

  const goPrev = () => setMonth((m) => m.subtract(1, "month"));
  const goNext = () => setMonth((m) => m.add(1, "month"));
  const goToday = () => setMonth(dayjs());

  const openDetail = (row) => {
    setDetailRow(row);
    setDetailOpen(true);
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setDetailRow(null);
  };

  const exportarAsistenciaXLSX = async () => {
    const matrizReporte = [
      [
        "Fecha",
        "ID tienda",
        "tienda",
        "Conductor Id",
        "Conductor Nombre",
        "turno",
        "attendanceId",
        "Incio programado",
        "Finalzación programada",
        "Inicio real",
        "Finalizacion Real",
        "Tiempo programado (min)",
        "Tiempo real (min)",
        "Inicio en horario",
        "Horas cumplidas",
        "localización OK",
        "Cerro dentro de la tienda",
        "Tiempo extra",
        "cumnplimiento",
      ],
      ...filteredRows.map((r) => {
        const driverName = driverMap.get(r.driverId)?.name || r.driverId;
        const storeName = storeMap.get(r.storeId)?.name || r.storeId;
        return [
          r.date || "",
          r.storeId || "",
          storeName,
          r.driverId || "",
          driverName,
          r.shiftId || "",
          r.attendanceId || "",
          r.scheduledStart || "",
          r.scheduledEnd || "",
          r.realStart || "",
          r.realEnd || "",
          r.scheduledMinutes ?? "",
          r.realMinutes ?? "",
          r.inHorario ?? "",
          r.horasCumplidas ?? "",
          r.geoOk ?? "",
          r.cerroDentro ?? "",
          r.tiempoExtra ?? "",
          r.compliance || "",
        ];
      }),
    ];

    const libro = XLSX.utils.book_new();
    const hoja = XLSX.utils.aoa_to_sheet(matrizReporte);
    XLSX.utils.book_append_sheet(libro, hoja, "Asistencia");

    const extra = [
      `_${month.format("YYYY-MM")}`,
      filterStoreId ? `_store-${filterStoreId}` : "",
      filterDriverId ? `_driver-${filterDriverId}` : "",
      filterCompliance ? `_cmp-${filterCompliance}` : "",
    ].join("");

    XLSX.writeFile(
      libro,
      `Asistencia${extra}_${moment(Date.now()).format(
        "YYYY-MM-DD_HH_mm_ss"
      )}.xlsx`
    );
  };

  const cardSx = {
    borderRadius: 3,
    border: "1px solid rgba(0,0,0,0.06)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
  };

  return (
    <Box
      sx={{ p: { xs: 1, md: 2 }, width: "100%", backgroundColor: "#F8F8F8" }}
    >
      <Grid2
        container
        spacing={2}
        sx={{
          width: "100%",
          backgroundColor: "#F8F8F8",
          p: "10px",
          justifyContent: "space-between",
        }}
      >
        {/* Header */}
        <Grid2 xs={12}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
          >
            <Box sx={{ flexGrow: 1, p: "10px", color: "#111" }}>
              <Typography variant="h5" sx={{ fontWeight: 900 }}>
                Asistencia
              </Typography>
              <ButtonLike
                sx={{ color: "#111" }}
                onClick={() => navigate(`/dashboard/turnos`)}
                icon={<ArrowBackIosNewIcon sx={{ color: "#09d31aff" }} />}
                text="ver turnos"
              />
              <Typography variant="body2" sx={{ opacity: 0.7, color: "#111" }}>
                Programado vs Marcado • Calendario mensual • Reportes
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title="Mes anterior">
                <IconButton onClick={goPrev}>
                  <ChevronLeftIcon />
                </IconButton>
              </Tooltip>

              <Typography
                sx={{
                  fontWeight: 900,
                  minWidth: 160,
                  textAlign: "center",
                  color: "#111",
                }}
              >
                {monthLabel}
              </Typography>

              <Tooltip title="Mes siguiente">
                <IconButton onClick={goNext}>
                  <ChevronRightIcon />
                </IconButton>
              </Tooltip>

              <ButtonLike
                onClick={goToday}
                icon={<TodayIcon sx={{ color: "#111" }} />}
                text="Hoy"
              />
            </Stack>
          </Stack>
        </Grid2>

        {/* Export */}
        <Grid2 xs={12}>
          <Card sx={{ ...cardSx, p: 2 }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ md: "center" }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 900 }}>
                  Reporte de asistencia
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                  Exporta lo que estás viendo (con filtros) a Excel. (Sin
                  creación/edición)
                </Typography>
              </Box>

              <Divider
                flexItem
                orientation="vertical"
                sx={{ display: { xs: "none", md: "block" } }}
              />

              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="flex-end"
              >
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={exportarAsistenciaXLSX}
                  disabled={loading}
                >
                  Descargar
                </Button>
              </Stack>
            </Stack>
          </Card>
        </Grid2>

        {/* Filtros */}
        <Grid2 xs={12}>
          <Card sx={{ ...cardSx, p: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Tienda</InputLabel>
                  <Select
                    label="Tienda"
                    value={filterStoreId}
                    onChange={(e) => setFilterStoreId(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Todas</em>
                    </MenuItem>
                    {storeOptions.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Conductor</InputLabel>
                  <Select
                    label="Conductor"
                    value={filterDriverId}
                    onChange={(e) => setFilterDriverId(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Todos</em>
                    </MenuItem>
                    {driverOptions.map((d) => (
                      <MenuItem key={d.id} value={d.id}>
                        {d.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Cumplimiento</InputLabel>
                  <Select
                    label="Compliance"
                    value={filterCompliance}
                    onChange={(e) => setFilterCompliance(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Todos</em>
                    </MenuItem>
                    <MenuItem value="OK">OK</MenuItem>
                    <MenuItem value="WARN">WARN</MenuItem>
                    <MenuItem value="BAD">BAD</MenuItem>
                    <MenuItem value="NO_ATT">SIN ASISTENCIA</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap">
              <Chip
                label="OK"
                sx={{ bgcolor: metaChip("ok").bg, color: metaChip("ok").fg }}
              />
              <Chip
                label="WARN"
                sx={{
                  bgcolor: metaChip("warn").bg,
                  color: metaChip("warn").fg,
                }}
              />
              <Chip
                label="BAD"
                sx={{ bgcolor: metaChip("bad").bg, color: metaChip("bad").fg }}
              />
              <Chip
                label="SIN ASISTENCIA"
                sx={{ bgcolor: metaChip("na").bg, color: metaChip("na").fg }}
              />
              <Box sx={{ flex: 1 }} />
              <Chip
                variant="outlined"
                label={
                  loading
                    ? "Cargando…"
                    : `${filteredRows.length} registro(s) (con filtros)`
                }
              />
            </Stack>
          </Card>
        </Grid2>

        {/* Calendario */}
        <Grid2 sx={{ width: "100%" }} xs={12}>
          <Card sx={{ ...cardSx, overflow: "hidden" }}>
            {/* Header semana */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                borderBottom: "1px solid rgba(0,0,0,0.06)",
                bgcolor: "rgba(0,0,0,0.02)",
              }}
            >
              {weekDays.map((w) => (
                <Box
                  key={w}
                  sx={{
                    p: 1,
                    textAlign: "center",
                    fontWeight: 900,
                    fontSize: 12,
                    opacity: 0.75,
                    borderRight: "1px solid rgba(0,0,0,0.04)",
                    "&:last-of-type": { borderRight: "none" },
                  }}
                >
                  {w}
                </Box>
              ))}
            </Box>

            {/* Grid días */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                gridAutoRows: { xs: "140px", md: "160px" },
              }}
            >
              {gridDays.map((d) => {
                const dateStr = d.format("YYYY-MM-DD");
                const isCurrentMonth = d.month() === month.month();
                const isToday = dateStr === dayjs().format("YYYY-MM-DD");
                const dayRows = rowsByDate.get(dateStr) || [];

                return (
                  <Box
                    key={dateStr}
                    sx={{
                      p: 1,
                      borderRight: "1px solid rgba(0,0,0,0.04)",
                      borderBottom: "1px solid rgba(0,0,0,0.04)",
                      bgcolor: isCurrentMonth ? "#fff" : "rgba(0,0,0,0.015)",
                      overflow: "hidden",
                    }}
                  >
                    {/* Día */}
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 900,
                          fontSize: 13,
                          bgcolor: isToday
                            ? "rgba(21,101,192,0.15)"
                            : "transparent",
                          color: isToday
                            ? "#1565C0"
                            : isCurrentMonth
                            ? "#111"
                            : "rgba(0,0,0,0.45)",
                        }}
                      >
                        {d.date()}
                      </Box>

                      {dayRows.length ? (
                        <Chip
                          size="small"
                          label={dayRows.length}
                          sx={{ height: 20, fontSize: 11, opacity: 0.8 }}
                        />
                      ) : null}
                    </Stack>

                    {/* Eventos */}
                    <Box
                      sx={{
                        mt: 0.75,
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.75,
                      }}
                    >
                      {dayRows.slice(0, 3).map((r) => {
                        const driverName =
                          driverMap.get(r.driverId)?.name ||
                          r.driverId ||
                          "Conductor";
                        const storeName =
                          storeMap.get(r.storeId)?.name ||
                          r.storeId ||
                          "Tienda";

                        const cmpKind =
                          r.compliance === "OK"
                            ? "ok"
                            : r.compliance === "WARN"
                            ? "warn"
                            : r.compliance === "BAD"
                            ? "bad"
                            : "na";
                        const cmp = metaChip(cmpKind);

                        const horarioChip =
                          r.inHorario === true
                            ? ["Horario OK", "ok"]
                            : r.inHorario === false
                            ? ["Horario", "bad"]
                            : ["Horario", "na"];

                        const horasChip =
                          r.horasCumplidas === true
                            ? ["Horas OK", "ok"]
                            : r.horasCumplidas === false
                            ? ["Horas", "bad"]
                            : ["Horas", "na"];

                        const geoChip =
                          r.geoOk === true
                            ? ["Geo OK", "ok"]
                            : r.geoOk === false
                            ? ["Geo", "bad"]
                            : ["Geo", "na"];

                        const extra = r.tiempoExtra ? ["Extra", "warn"] : null;
                        const cierreFuera = r.cierreFuera
                          ? ["Cierre fuera", "warn"]
                          : null;

                        const label = `${r.scheduledStart || ""}-${
                          r.scheduledEnd || ""
                        }`;

                        const st = statusMeta(r.shiftStatus);

                        return (
                          <Tooltip
                            key={r.shiftId}
                            title={`${label} • ${driverName} • ${storeName} • ${r.compliance}`}
                            arrow
                          >
                            <Box
                              onClick={() => openDetail(r)}
                              sx={{
                                px: 0.75,
                                py: 0.6,
                                borderRadius: 2,
                                border: `1px solid ${st.bd}`,
                                bgcolor: st.bg,
                                color: st.fg,
                                cursor: "pointer",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: 11,
                                  fontWeight: 900,
                                  lineHeight: 1.2,
                                }}
                                noWrap
                              >
                                {label} • {driverName} — {storeName}
                              </Typography>

                              <Stack
                                direction="row"
                                spacing={0.5}
                                sx={{ mt: 0.5, flexWrap: "wrap" }}
                              >
                                {[horarioChip, horasChip, geoChip].map(
                                  ([t, k]) => {
                                    const m = metaChip(k);
                                    return (
                                      <Chip
                                        key={t}
                                        size="small"
                                        label={t}
                                        sx={{
                                          height: 20,
                                          fontSize: 10,
                                          bgcolor: m.bg,
                                          color: m.fg,
                                          border: `1px solid ${st.bd}`,
                                        }}
                                      />
                                    );
                                  }
                                )}
                                {extra ? (
                                  <Chip
                                    size="small"
                                    label={extra[0]}
                                    sx={{
                                      height: 20,
                                      fontSize: 10,
                                      bgcolor: metaChip("warn").bg,
                                      color: metaChip("warn").fg,
                                    }}
                                  />
                                ) : null}
                                {cierreFuera ? (
                                  <Chip
                                    size="small"
                                    label={cierreFuera[0]}
                                    sx={{
                                      height: 20,
                                      fontSize: 10,
                                      bgcolor: metaChip("warn").bg,
                                      color: metaChip("warn").fg,
                                    }}
                                  />
                                ) : null}
                              </Stack>
                            </Box>
                          </Tooltip>
                        );
                      })}

                      {dayRows.length > 3 ? (
                        <Typography
                          variant="caption"
                          sx={{ opacity: 0.7, mt: 0.25 }}
                        >
                          +{dayRows.length - 3} más…
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {loading ? (
              <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
                <CircularProgress size={22} />
              </Box>
            ) : null}
          </Card>
        </Grid2>
      </Grid2>

      {/* Detail modal (solo lectura) */}
      <Dialog open={detailOpen} onClose={closeDetail} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 900 }}>Detalle asistencia</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {detailRow ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Card
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid rgba(0,0,0,0.06)",
                }}
              >
                <Typography sx={{ fontWeight: 900 }}>
                  {detailRow.date} •{" "}
                  {storeMap.get(detailRow.storeId)?.name || detailRow.storeId} •{" "}
                  {driverMap.get(detailRow.driverId)?.name ||
                    detailRow.driverId}
                </Typography>

                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={2}
                  sx={{ mt: 1 }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      Programado
                    </Typography>
                    <Typography sx={{ fontWeight: 900 }}>
                      {detailRow.scheduledStart} - {detailRow.scheduledEnd} (
                      {detailRow.scheduledMinutes ?? "?"} min)
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      Marcado
                    </Typography>
                    <Typography sx={{ fontWeight: 900 }}>
                      {detailRow.realStart || "—"} - {detailRow.realEnd || "—"}{" "}
                      ({detailRow.realMinutes ?? "?"} min)
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      Compliance
                    </Typography>
                    <Typography sx={{ fontWeight: 900 }}>
                      {detailRow.compliance}
                    </Typography>
                  </Box>
                </Stack>
              </Card>

              <Card
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid rgba(0,0,0,0.06)",
                }}
              >
                <Typography sx={{ fontWeight: 900 }}>Checks</Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                  sx={{ mt: 1 }}
                >
                  <Chip
                    label={`Horario: ${
                      detailRow.inHorario === null
                        ? "N/A"
                        : detailRow.inHorario
                        ? "OK"
                        : "NO"
                    }`}
                  />
                  <Chip
                    label={`Horas: ${
                      detailRow.horasCumplidas === null
                        ? "N/A"
                        : detailRow.horasCumplidas
                        ? "OK"
                        : "NO"
                    }`}
                  />
                  <Chip
                    label={`Geo: ${
                      detailRow.geoOk === null
                        ? "N/A"
                        : detailRow.geoOk
                        ? "OK"
                        : "NO"
                    }`}
                  />
                  <Chip
                    label={`Cierre fuera: ${
                      detailRow.cierreFuera ? "SI" : "NO"
                    }`}
                  />
                  <Chip
                    label={`Tiempo extra: ${
                      detailRow.tiempoExtra ? "SI" : "NO"
                    }`}
                  />
                </Stack>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ opacity: 0.7 }}>
                    (Luego aquí agregamos: movilizaciones fuera + motivo,
                    entradas a tiendas/visitas, cierre fuera de tienda, etc.)
                  </Typography>
                </Box>
              </Card>

              {detailRow.attendance?.mobilizations?.length ? (
                <Card
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: "1px solid rgba(0,0,0,0.06)",
                  }}
                >
                  <Typography sx={{ fontWeight: 900 }}>
                    Eventos / Movilizaciones (raw)
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      mt: 1,
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: "rgba(0,0,0,0.03)",
                      overflow: "auto",
                      fontSize: 12,
                      maxHeight: 240,
                    }}
                  >
                    {JSON.stringify(
                      detailRow.attendance.mobilizations,
                      null,
                      2
                    )}
                  </Box>
                </Card>
              ) : null}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeDetail} variant="outlined">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToast((p) => ({ ...p, open: false }))}
          severity={toast.severity}
          sx={{ width: "100%" }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Botón mini simple
const ButtonLike = ({ onClick, icon, text }) => (
  <Box
    onClick={onClick}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 1,
      px: 1.25,
      py: 0.75,
      borderRadius: 2,
      border: "1px solid rgba(0,0,0,0.12)",
      cursor: "pointer",
      userSelect: "none",
      "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
    }}
  >
    {icon}
    <Typography sx={{ fontSize: 13, fontWeight: 900 }}>{text}</Typography>
  </Box>
);

export default Asistencia;
